// Предметы и их источники: шансы в тайниках, дроп с врагов, ожидания за
// уровень и за акт, «покрутить» веса и бюджеты. Контракт, §3b.
//
// ВСЕ ФОРМУЛЫ ЗДЕСЬ, А НЕ НА СЕРВЕРЕ. Сервер (GET /api/items) отдаёт сырые
// числа ассетов; пересчёт на лету при движении поля должен давать ровно
// тот же ответ, что и первый показ, — значит формулы в одном месте.
//
// ЧТО ЭТО ЗА ЧИСЛА. Это АРИФМЕТИКА по данным, а не симуляция: вероятность
// предмета внутри таблицы точна (тот же разворот тег-строк, что у
// LootTableDef.TryToValues), а ожидание «штук за уровень» — среднее броска
// бюджета без учёта того, что уровень может переполниться, и без учёта
// планировки. Там, где число зависит от планировки (патроны — от числа
// врагов), отдаётся диапазон по границам бюджета, а не одно число.

// --- модель, как её отдаёт сервер ---------------------------------------------

export interface AssetRef {
  name: string;
  path: string;
}

export interface ItemAspect {
  /** Имя класса аспекта; null — тип потерян (переименование без [MovedFrom]). */
  type: string | null;
  /**
   * Поля аспекта без `_`. У ConsumableAspect сервер добавляет `effectKind` — имя
   * ConsumableEffectKind ('Heal', 'RechargeFlashlight', 'HealToFull'…): таблица
   * чисел эффекта живёт в одном месте, в server/src/items.js.
   */
  data: Record<string, string | number | null>;
}

export interface ItemModel {
  asset: AssetRef;
  guid: string | null;
  id: string;
  displayName: string | null;
  enabled: boolean;
  inCatalog: boolean;
  placeholder: boolean;
  tags: string[];
  sourcedByScript: boolean;
  sourcedByScriptNote: string;
  stackable: boolean;
  stackLimit: number;
  talonDenomination: number;
  aspects: ItemAspect[];
}

export interface LootRule {
  type: string;
  data: Record<string, string | number | null>;
}

export interface LootCategoryModel {
  asset: AssetRef;
  id: string;
  displayName: string | null;
  rule: LootRule | null;
}

export interface LootEntryModel {
  itemId: string | null;
  tagId: string | null;
  weight: number;
}

export interface LootTableModel {
  asset: AssetRef;
  category: string | null;
  act: number;
  theme: string | null;
  inLootCatalog: boolean;
  placeholder: boolean;
  entries: LootEntryModel[];
}

export interface LootRowModel {
  category: string | null;
  min: number;
  max: number;
  stackMin: number;
  stackMax: number;
}

export interface BudgetModel {
  asset: AssetRef;
  id: string | null;
  displayName: string | null;
  minEnemies: number;
  maxEnemies: number;
  enemiesPerHundredCells: number;
  averageShotsPerEnemy: number;
  ammoBudgetPermille: number;
  lootClassWeightBonus: number;
  fuseItem: string | null;
  /** «Слот ПМ этажа» (CompositionRoot.ConfigurePistolFind): шанс, %, найти ПМ без ствола / запасной. */
  pistolFindChanceUnarmedPercent: number;
  pistolSpareFindChancePercent: number;
  lootRows: LootRowModel[];
  /** Номера уровней акта, которые берут этот бюджет (SourceCatalogDef._levelBudgets). */
  levels: number[];
  /** Общий бюджет-фолбэк (_levelBudgetDef). */
  fallback: boolean;
}

export interface EnemyModel {
  asset: AssetRef;
  id: string | null;
  displayName: string | null;
  /** Назначен в SourceCatalogDef._zombieDef — именно он ходит по уровням. */
  assigned: boolean;
  dropTable: string | null;
  dropChancePercent: number;
}

export interface BundleModel {
  role: 'hubTable' | 'restSafe' | 'startingKit' | string;
  asset: AssetRef;
  entries: Array<{ itemId: string | null; count: number; loadedAmmo: number }>;
}

export interface RecipeModel {
  asset: AssetRef;
  first: string | null;
  second: string | null;
  result: string | null;
}

export interface ShopEntryModel {
  asset: AssetRef;
  id: string | null;
  itemId: string | null;
  count: number;
  priceTalons: number;
  priceApproved: boolean;
  quoteRevision: string;
  onSale: boolean;
}

export interface RoomModuleModel {
  asset: AssetRef;
  id: string | null;
  actId: string | null;
  selectionWeight: number;
  lootPoints?: number;
  instanceLimit?: number;
  lootCategory: string | null;
  directorKeyItem: string | null;
  directorSafeAidKitItem: string | null;
}

export interface ItemsPayload {
  readOnly: boolean;
  writePolicy: string;
  productionAct: number;
  generatedAtUtc: string;
  items: ItemModel[];
  categories: LootCategoryModel[];
  tables: LootTableModel[];
  budgets: BudgetModel[];
  enemies: EnemyModel[];
  roles: { furnitureMedicine: string | null; enemyFallbackDrop: string | null; balanceCurrency: string | null };
  bundles: BundleModel[];
  recipes: RecipeModel[];
  shop: ShopEntryModel[];
  roomModules: RoomModuleModel[];
  /** SourceCatalogDef._bossFloorBudget — этаж хозяина. В назначенные бюджеты не входит, как и в отчёте Unity. */
  bossFloorBudget: BudgetModel | null;
  /** SourceCatalogDef._pistolFloorFind — item_id, который кладёт «слот ПМ этажа». */
  pistolFloorFind: string | null;
  /** SourceCatalogDef._nurseHubBalance — находки Веры на этаже. */
  nurseBalance: NurseBalanceModel | null;
  warnings: string[];
}

export interface NurseBalanceModel {
  asset: AssetRef;
  keysItemId: string | null;
  fieldCaseItemId: string | null;
  keysFindChance: number;
  fieldCaseFindChance: number;
}

// --- аспекты словами ---------------------------------------------------------------

export function aspectSummary(aspect: ItemAspect): string {
  const d = aspect.data;
  switch (aspect.type) {
    case null:
      return 'аспект потерян (null)';
    case 'ConsumableAspect':
      if (d.effectKind === 'Heal') return `лечение ${d.amount ?? '?'}`;
      if (d.effectKind === 'RechargeFlashlight') return `заряд фонаря ${d.amount ?? '?'}%`;
      if (d.effectKind === 'HealToFull') return 'лечение до полного';
      return `расходник: эффект ${d.effect ?? '?'} = ${d.amount ?? '?'}`;
    case 'RangedWeaponAspect':
      return `огнестрел: урон ${d.damage ?? '?'}, магазин ${d.magazineCapacity ?? '?'}, патрон ${d.ammo ?? 'не задан'}`;
    case 'MeleeWeaponAspect':
      return `холодное: урон ${d.damage ?? '?'}`;
    default:
      return aspect.type.replace(/Aspect$/, '');
  }
}

// --- «покрутить»: ручки и правки -----------------------------------------------------

/** Одна ручка — одно поле одного ассета, которое можно поменять во вкладке. */
export interface Knob {
  key: string;
  asset: AssetRef;
  /** Путь поля, как его видно в YAML/инспекторе: `_entries[1]._weight`. */
  field: string;
  /** Человеческое уточнение: что это за строка. */
  label: string;
  base: number;
  min: number;
  max: number;
}

export type Edits = Readonly<Record<string, number>>;

/**
 * Какие числа крутятся во вкладке. Границы повторяют проверки ассетов:
 * вес строки лута < 1 — ошибка LootTableDef.IsValid («удалять, а не
 * обнулять»), ‰ патронов — [0, 999] (PopulationBudget), шанс — проценты.
 */
export function listKnobs(model: ItemsPayload): Knob[] {
  const knobs: Knob[] = [];
  const rules = new Map(model.categories.map((category) => [category.id, category.rule?.type ?? null]));

  for (const table of model.tables) {
    table.entries.forEach((entry, index) => {
      knobs.push({
        key: tableKey(table.asset.name, index),
        asset: table.asset,
        field: `_entries[${index}]._weight`,
        label: entry.itemId ?? (entry.tagId !== null ? `тег ${entry.tagId}` : 'ничего'),
        base: entry.weight,
        min: 1,
        max: 1000,
      });
    });
  }

  for (const budget of model.budgets) {
    budget.lootRows.forEach((row, index) => {
      // Строку патронов правило дефицита не читает (число — от врагов),
      // и ручка на неё врала бы, что что-то меняет.
      if (row.category !== null && rules.get(row.category) === AMMO_RULE) return;
      for (const bound of ['min', 'max'] as const) {
        knobs.push({
          key: budgetRowKey(budget.asset.name, index, bound),
          asset: budget.asset,
          field: `_lootRows[${index}]._${bound}`,
          label: `${row.category ?? '—'} ${bound === 'min' ? 'мин' : 'макс'}`,
          base: row[bound],
          min: 0,
          max: 50,
        });
      }
    });
    const fields: Array<[keyof BudgetModel & string, string, number, number]> = [
      ['averageShotsPerEnemy', 'выстрелов на врага', 1, 30],
      ['ammoBudgetPermille', 'бюджет патронов, ‰', 0, 999],
      ['minEnemies', 'врагов мин', 0, 40],
      ['maxEnemies', 'врагов макс', 0, 40],
    ];
    for (const [field, label, min, max] of fields) {
      knobs.push({
        key: budgetFieldKey(budget.asset.name, field),
        asset: budget.asset,
        field: `_${field}`,
        label,
        base: budget[field] as number,
        min,
        max,
      });
    }
  }

  for (const enemy of model.enemies) {
    knobs.push({
      key: enemyKey(enemy.asset.name),
      asset: enemy.asset,
      field: '_dropChancePercent',
      label: 'шанс дропа, %',
      base: enemy.dropChancePercent,
      min: 0,
      max: 100,
    });
  }

  return knobs;
}

export const tableKey = (table: string, index: number) => `table:${table}:${index}`;
export const budgetRowKey = (budget: string, index: number, bound: 'min' | 'max') => `budget:${budget}:row${index}:${bound}`;
export const budgetFieldKey = (budget: string, field: string) => `budget:${budget}:${field}`;
export const enemyKey = (enemy: string) => `enemy:${enemy}:drop`;

/** Модель с применёнными правками. Исходник не меняется. */
export function applyEdits(model: ItemsPayload, edits: Edits): ItemsPayload {
  const pick = (key: string, fallback: number) => (key in edits ? (edits[key] as number) : fallback);
  return {
    ...model,
    tables: model.tables.map((table) => ({
      ...table,
      entries: table.entries.map((entry, index) => ({ ...entry, weight: pick(tableKey(table.asset.name, index), entry.weight) })),
    })),
    budgets: model.budgets.map((budget) => ({
      ...budget,
      averageShotsPerEnemy: pick(budgetFieldKey(budget.asset.name, 'averageShotsPerEnemy'), budget.averageShotsPerEnemy),
      ammoBudgetPermille: pick(budgetFieldKey(budget.asset.name, 'ammoBudgetPermille'), budget.ammoBudgetPermille),
      minEnemies: pick(budgetFieldKey(budget.asset.name, 'minEnemies'), budget.minEnemies),
      maxEnemies: pick(budgetFieldKey(budget.asset.name, 'maxEnemies'), budget.maxEnemies),
      lootRows: budget.lootRows.map((row, index) => ({
        ...row,
        min: pick(budgetRowKey(budget.asset.name, index, 'min'), row.min),
        max: pick(budgetRowKey(budget.asset.name, index, 'max'), row.max),
      })),
    })),
    enemies: model.enemies.map((enemy) => ({
      ...enemy,
      dropChancePercent: pick(enemyKey(enemy.asset.name), enemy.dropChancePercent),
    })),
  };
}

export interface Change {
  asset: AssetRef;
  field: string;
  label: string;
  from: number;
  to: number;
}

/** Список «ассет · поле · было · стало» — только то, что реально отличается. */
export function changeList(knobs: readonly Knob[], edits: Edits): Change[] {
  const changes: Change[] = [];
  for (const knob of knobs) {
    if (!(knob.key in edits)) continue;
    const to = edits[knob.key] as number;
    if (to === knob.base) continue;
    changes.push({ asset: knob.asset, field: knob.field, label: knob.label, from: knob.base, to });
  }
  return changes;
}

/** Текст для буфера: одна строка на поле, путь ассета — чтобы найти его в Project. */
export function formatChanges(changes: readonly Change[]): string {
  if (changes.length === 0) return '';
  const lines = changes.map(
    (change) => `${change.asset.name} · ${change.field} (${change.label}) · ${change.from} → ${change.to}    ${change.asset.path}`,
  );
  return (
    '# СПУСК · вкладка «Предметы»: перенести руками в инспекторе (ADR-0023 — приложение в .asset не пишет)\n' +
    lines.join('\n') +
    '\n'
  );
}

/** Проверки правленой модели — того же смысла, что у ассетов; нарушение показывается, не блокирует пересчёт. */
export function validateModel(model: ItemsPayload): string[] {
  const errors: string[] = [];
  for (const budget of model.budgets) {
    budget.lootRows.forEach((row, index) => {
      if (row.max < row.min) errors.push(`${budget.asset.name}: строка ${index} (${row.category}) — макс ${row.max} меньше мин ${row.min}.`);
    });
    if (budget.maxEnemies < budget.minEnemies)
      errors.push(`${budget.asset.name}: врагов макс ${budget.maxEnemies} меньше мин ${budget.minEnemies}.`);
    if (budget.ammoBudgetPermille > 999)
      errors.push(`${budget.asset.name}: ‰ патронов вне [0, 999] — патронов не может быть столько же, сколько нужно.`);
  }
  for (const table of model.tables) {
    table.entries.forEach((entry, index) => {
      if (entry.weight < 1) errors.push(`${table.asset.name}: строка ${index} — вес ${entry.weight}; строку удаляют, а не обнуляют.`);
    });
  }
  return errors;
}

// --- таблицы: разворот тегов и вероятность --------------------------------------------

export interface ExpandedRow {
  /** Строка ассета, из которой получена эта. */
  rowIndex: number;
  itemId: string | null;
  viaTag: string | null;
  /** Вес после масштабирования на НОК размеров тегов. */
  weight: number;
}

export interface ExpandedTable {
  rows: ExpandedRow[];
  total: number;
  /** Тег, не развернувшийся ни во что, и подобные ошибки сборки. */
  error: string | null;
}

const ordinal = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/** ItemTagDef.EnabledItemsIn: включённые предметы каталога с тегом, по item_id ordinal. */
export function tagMembers(model: Pick<ItemsPayload, 'items'>, tag: string): string[] {
  const ids = new Set<string>();
  for (const item of model.items) {
    if (item.inCatalog && item.enabled && item.tags.includes(tag)) ids.add(item.id);
  }
  return [...ids].sort(ordinal);
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

/**
 * Разворот таблицы — тот же, что LootTableDef.TryToValues: тег-строка веса W
 * на n предметов даёт каждому W·L/n, явная строка — w·L, L = НОК всех n.
 * Отношения весов точные, без округления.
 */
export function expandTable(model: Pick<ItemsPayload, 'items'>, table: LootTableModel): ExpandedTable {
  const members = table.entries.map((entry) => (entry.tagId !== null && entry.itemId === null ? tagMembers(model, entry.tagId) : null));
  let scale = 1;
  for (let i = 0; i < members.length; i++) {
    const list = members[i];
    if (list === null || list === undefined) continue;
    if (list.length === 0) {
      return { rows: [], total: 0, error: `строка ${i}: тег '${table.entries[i]?.tagId}' не разворачивается ни в один включённый предмет каталога` };
    }
    scale = (scale / gcd(scale, list.length)) * list.length;
  }

  const rows: ExpandedRow[] = [];
  let total = 0;
  table.entries.forEach((entry, rowIndex) => {
    const list = members[rowIndex];
    const weight = Math.max(0, entry.weight);
    if (list === null || list === undefined) {
      rows.push({ rowIndex, itemId: entry.itemId, viaTag: null, weight: weight * scale });
      total += weight * scale;
      return;
    }
    const share = (weight * scale) / list.length;
    for (const itemId of list) {
      rows.push({ rowIndex, itemId, viaTag: entry.tagId, weight: share });
      total += share;
    }
  });
  return { rows, total, error: null };
}

/** Доля предмета в таблице: вес его строк / сумма весов. */
export function itemShare(expanded: ExpandedTable, itemId: string): number {
  if (expanded.total <= 0) return 0;
  let weight = 0;
  for (const row of expanded.rows) if (row.itemId === itemId) weight += row.weight;
  return weight / expanded.total;
}

// --- какая таблица работает в забеге ------------------------------------------------------

export type TableStatus =
  | 'active'
  | 'not-in-loot-catalog'
  | 'no-category'
  | 'theme-deferred'
  | 'other-act'
  | 'shadowed'
  | 'ambiguous'
  | 'no-budget-row';

export const TABLE_STATUS_TEXT: Record<TableStatus, string> = {
  active: 'работает',
  'not-in-loot-catalog': 'нет в LootCatalog — тайники её не видят',
  'no-category': 'без категории — не перенесена в каталог лута',
  'theme-deferred': 'тематическая — отложено (ADR-0025 §4): генератор тему не знает, таблица не срабатывает',
  'other-act': 'другого акта — в забеге этого акта не участвует',
  shadowed: 'перекрыта более точной таблицей той же категории',
  ambiguous: 'две таблицы одной точности на категорию — ошибка сборки',
  'no-budget-row': 'категории нет ни в одном назначенном бюджете',
};

/**
 * Статус таблицы как источника тайника в акте `act`. Выбор — LootTableSet:
 * самая точная из подходящих (тема+акт > тема > акт > любая); тематические
 * отложены, поэтому из них не выигрывает ни одна.
 */
export function tableStatus(model: ItemsPayload, table: LootTableModel, act = model.productionAct): TableStatus {
  if (!table.inLootCatalog) return 'not-in-loot-catalog';
  if (table.category === null) return 'no-category';
  if (table.theme !== null) return 'theme-deferred';
  if (table.act !== 0 && table.act !== act) return 'other-act';

  const rivals = model.tables.filter(
    (other) => other.inLootCatalog && other.category === table.category && other.theme === null && (other.act === 0 || other.act === act),
  );
  const best = rivals.some((other) => other.act === act) ? act : 0;
  if (table.act !== best) return 'shadowed';
  if (rivals.filter((other) => other.act === best).length > 1) return 'ambiguous';

  const inBudget = model.budgets.some((budget) => budget.lootRows.some((row) => row.category === table.category));
  return inBudget ? 'active' : 'no-budget-row';
}

/**
 * Таблица, которую отчёт достижимости засчитывает источником
 * (ItemReachabilityReport.FilterAssignedLootTables): в каталоге лута,
 * категория есть в строке назначенного бюджета, акт 0 или акт забега.
 * Тему и перекрытие отчёт не смотрит — и мы здесь тоже, чтобы
 * предупреждение совпадало с Validate Content слово в слово.
 */
export function isAssignedTable(model: ItemsPayload, table: LootTableModel, act = model.productionAct): boolean {
  if (!table.inLootCatalog || table.category === null) return false;
  if (table.act !== 0 && table.act !== act) return false;
  return model.budgets.some((budget) => budget.lootRows.some((row) => row.category === table.category));
}

/** Рабочая таблица категории в акте, если есть. */
export function activeTable(model: ItemsPayload, category: string, act = model.productionAct): LootTableModel | null {
  return model.tables.find((table) => table.category === category && tableStatus(model, table, act) === 'active') ?? null;
}

// --- сколько за уровень ---------------------------------------------------------------------

export const AMMO_RULE = 'AmmoDeficitLootRule';
export const SCATTER_RULE = 'ScatterLootRule';
export const KEY_RULE = 'KeyBeforeLockLootRule';

/** Диапазон: low/high — по границам бюджета, mid — точка, которую показываем числом. */
export interface Estimate {
  low: number;
  mid: number;
  high: number;
}

const point = (value: number): Estimate => ({ low: value, mid: value, high: value });
const scale = (estimate: Estimate, factor: number): Estimate => ({
  low: estimate.low * factor,
  mid: estimate.mid * factor,
  high: estimate.high * factor,
});
const add = (a: Estimate, b: Estimate): Estimate => ({ low: a.low + b.low, mid: a.mid + b.mid, high: a.high + b.high });
export const ZERO: Estimate = point(0);

/**
 * Патронов на уровень: LevelPopulator.AmmoAllowance — врагов × расход × ‰,
 * округление вниз, и строго меньше врагов × расход (TR-CMB-05).
 */
export function ammoAllowance(enemies: number, averageShots: number, permille: number): number {
  const ceiling = enemies * averageShots;
  if (ceiling <= 0) return 0;
  const budgeted = Math.floor((ceiling * permille) / 1000);
  return Math.min(budgeted, ceiling - 1);
}

export interface RowExpectation {
  /** Сколько находок категории (не штук). */
  finds: Estimate;
  /** Сколько штук категории (патронов, талонов…) — до умножения на долю предмета. */
  units: Estimate;
  /** Почему число не точное — показывается рядом. */
  note: string;
  /** Число зависит от планировки — честно считать только симуляцией. */
  dependsOnLayout: boolean;
}

/**
 * Ожидание по одной строке бюджета. Не учитывает переполнение уровня
 * (context.IsFull) — это верхняя оценка при тесном уровне.
 */
export function expectRow(rule: LootRule | null, row: LootRowModel, budget: BudgetModel): RowExpectation | null {
  if (rule === null) return null;
  const stackMean = (row.stackMin + row.stackMax) / 2;
  const countMean = (row.min + row.max) / 2;

  if (rule.type === AMMO_RULE) {
    const low = ammoAllowance(budget.minEnemies, budget.averageShotsPerEnemy, budget.ammoBudgetPermille);
    const high = ammoAllowance(budget.maxEnemies, budget.averageShotsPerEnemy, budget.ammoBudgetPermille);
    const units: Estimate = { low, mid: (low + high) / 2, high };
    const perFind = Math.max(1, stackMean);
    return {
      finds: { low: Math.ceil(low / perFind), mid: units.mid / perFind, high: Math.ceil(high / perFind) },
      units,
      note:
        `патроны от врагов: врагов × ${budget.averageShotsPerEnemy} выстр. × ${budget.ammoBudgetPermille}‰, ` +
        `строго меньше потребности; врагов ${budget.minEnemies}–${budget.maxEnemies} по планировке — ` +
        'точное число только симуляцией. Мин/макс строки правило не читает.',
      dependsOnLayout: true,
    };
  }

  if (rule.type === KEY_RULE) {
    return {
      finds: { low: row.min, mid: countMean, high: row.max },
      units: { low: row.min, mid: countMean, high: row.max },
      note: 'ключ: при запертой двери не меньше 1, без замка находка снимается — зависит от планировки',
      dependsOnLayout: true,
    };
  }

  if (rule.type === SCATTER_RULE) {
    const stacked = rule.data.stacked === 1;
    const perFind = stacked ? stackMean : 1;
    return {
      finds: { low: row.min, mid: countMean, high: row.max },
      units: { low: row.min * (stacked ? row.stackMin : 1), mid: countMean * perFind, high: row.max * (stacked ? row.stackMax : 1) },
      note: stacked ? `находок ${row.min}–${row.max}, в каждой ${row.stackMin}–${row.stackMax} шт.` : `находок ${row.min}–${row.max}, по 1 шт.`,
      dependsOnLayout: false,
    };
  }

  return null;
}

// --- сводка по предмету -------------------------------------------------------------------

export interface StashLine {
  table: LootTableModel;
  status: TableStatus;
  category: LootCategoryModel | null;
  /** Строки ассета, дающие предмет (явно или тегом), с исходным весом. */
  rows: Array<{ rowIndex: number; weight: number; viaTag: string | null }>;
  tableWeight: number;
  share: number;
  error: string | null;
  perBudget: Array<{ budget: BudgetModel; expected: Estimate | null; note: string; dependsOnLayout: boolean }>;
  /** Ожидание за акт: сумма по уровням 1..N (по фолбэку, если уровней нет). */
  perAct: Estimate | null;
  actLevels: number;
  /** Модули, которые предпочитают категорию этой таблицы. */
  preferredBy: RoomModuleModel[];
  furnitureMedicine: boolean;
}

export interface DropLine {
  enemy: EnemyModel;
  table: LootTableModel | null;
  viaFallback: boolean;
  share: number;
  perKill: number;
  /** Если убить всех врагов уровня — по назначенному бюджету. */
  perBudget: Array<{ budget: BudgetModel; expected: Estimate }>;
}

export interface Place {
  kind: 'module' | 'budget' | 'bundle' | 'recipe' | 'shop' | 'floor';
  asset: AssetRef;
  text: string;
  /** Считается ли источником в отчёте достижимости. */
  source: boolean;
}

export interface ItemSummary {
  item: ItemModel;
  aspects: string[];
  stash: StashLine[];
  drops: DropLine[];
  places: Place[];
  shop: ShopEntryModel[];
  /** Источники в смысле ItemReachabilityReport — строками. */
  sources: string[];
  unreachable: boolean;
  /** Источник формально есть, но при этих числах предмет не появляется. */
  starved: boolean;
  /** Ожидаемо штук за акт из тайников (рабочие таблицы) — для сортировки и плитки. */
  stashPerAct: Estimate;
}

/** Бюджеты, реально играемые уровнями акта: уровни 1..N, иначе фолбэк один раз. */
export function actBudgets(model: ItemsPayload): Array<{ level: number | null; budget: BudgetModel }> {
  const byLevel: Array<{ level: number | null; budget: BudgetModel }> = [];
  for (const budget of model.budgets) for (const level of budget.levels) byLevel.push({ level, budget });
  byLevel.sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
  if (byLevel.length > 0) return byLevel;
  const fallback = model.budgets.find((budget) => budget.fallback);
  return fallback === undefined ? [] : [{ level: null, budget: fallback }];
}

/** Строка лута даёт предмет: явной ссылкой или тегом (включённый предмет каталога с тегом). */
function rowGives(entry: LootEntryModel, item: ItemModel): boolean {
  if (entry.itemId !== null) return entry.itemId === item.id;
  return entry.tagId !== null && item.inCatalog && item.enabled && item.tags.includes(entry.tagId);
}

export function summarizeItem(model: ItemsPayload, item: ItemModel): ItemSummary {
  const categories = new Map(model.categories.map((category) => [category.id, category]));
  const levels = actBudgets(model);
  const expandedCache = new Map<string, ExpandedTable>();
  const expand = (table: LootTableModel) => {
    let expanded = expandedCache.get(table.asset.path);
    if (expanded === undefined) {
      expanded = expandTable(model, table);
      expandedCache.set(table.asset.path, expanded);
    }
    return expanded;
  };

  // Тайники: все таблицы, где предмет встречается, — со статусом.
  const stash: StashLine[] = [];
  for (const table of model.tables) {
    const expanded = expand(table);
    const rows: StashLine['rows'] = [];
    table.entries.forEach((entry, rowIndex) => {
      if (rowGives(entry, item)) rows.push({ rowIndex, weight: entry.weight, viaTag: entry.itemId === null ? entry.tagId : null });
    });
    if (rows.length === 0) continue;

    const status = tableStatus(model, table);
    const category = table.category === null ? null : (categories.get(table.category) ?? null);
    const share = itemShare(expanded, item.id);
    const perBudget: StashLine['perBudget'] = [];
    for (const budget of model.budgets) {
      budget.lootRows.forEach((row) => {
        if (row.category !== table.category) return;
        const expectation = status === 'active' ? expectRow(category?.rule ?? null, row, budget) : null;
        perBudget.push({
          budget,
          expected: expectation === null ? null : scale(expectation.units, share),
          note:
            status !== 'active'
              ? TABLE_STATUS_TEXT[status]
              : expectation === null
                ? `правило ${category?.rule?.type ?? '—'} здесь не считается — только симуляция`
                : expectation.note,
          dependsOnLayout: expectation?.dependsOnLayout ?? true,
        });
      });
    }

    let perAct: Estimate | null = status === 'active' ? ZERO : null;
    if (perAct !== null) {
      for (const { budget } of levels) {
        const hit = perBudget.find((entry) => entry.budget === budget);
        if (hit === undefined) continue;
        if (hit.expected === null) {
          perAct = null;
          break;
        }
        perAct = add(perAct, hit.expected);
      }
    }

    stash.push({
      table,
      status,
      category,
      rows,
      tableWeight: table.entries.reduce((sum, entry) => sum + Math.max(0, entry.weight), 0),
      share,
      error: expanded.error,
      perBudget,
      perAct,
      actLevels: levels.length,
      preferredBy: table.category === null ? [] : model.roomModules.filter((module) => module.lootCategory === table.category),
      furnitureMedicine: status === 'active' && table.category !== null && table.category === model.roles.furnitureMedicine,
    });
  }

  // Дроп с врагов: своя таблица или таблица категории запасного дропа.
  const drops: DropLine[] = [];
  for (const enemy of model.enemies) {
    let table = enemy.dropTable === null ? null : (model.tables.find((candidate) => candidate.asset.name === enemy.dropTable) ?? null);
    let viaFallback = false;
    if (table === null && enemy.dropTable === null && model.roles.enemyFallbackDrop !== null) {
      table = activeTable(model, model.roles.enemyFallbackDrop);
      viaFallback = table !== null;
    }
    if (table === null) continue;
    const share = itemShare(expand(table), item.id);
    if (share === 0) continue;
    const perKill = (Math.max(0, Math.min(100, enemy.dropChancePercent)) / 100) * share;
    drops.push({
      enemy,
      table,
      viaFallback,
      share,
      perKill,
      perBudget: enemy.assigned
        ? model.budgets.map((budget) => ({
            budget,
            expected: { low: perKill * budget.minEnemies, mid: (perKill * (budget.minEnemies + budget.maxEnemies)) / 2, high: perKill * budget.maxEnemies },
          }))
        : [],
    });
  }

  // Адресные места, наборы, рецепты, прилавок.
  const places: Place[] = [];
  for (const module of model.roomModules) {
    if (module.directorKeyItem === item.id)
      places.push({ kind: 'module', asset: module.asset, text: 'ключ кабинета начальника (паспорт модуля)', source: true });
    if (module.directorSafeAidKitItem === item.id)
      places.push({ kind: 'module', asset: module.asset, text: 'аптечка сейфа кабинета начальника (паспорт модуля)', source: true });
  }
  for (const budget of model.budgets) {
    if (budget.fuseItem === item.id) places.push({ kind: 'budget', asset: budget.asset, text: 'предохранитель щита лифта (бюджет уровня)', source: true });
  }
  const bundleNames: Record<string, string> = { hubTable: 'стол хаба', restSafe: 'сейф комнаты отдыха', startingKit: 'стартовый набор' };
  for (const bundle of model.bundles) {
    for (const entry of bundle.entries) {
      if (entry.itemId !== item.id) continue;
      places.push({
        kind: 'bundle',
        asset: bundle.asset,
        text: `${bundleNames[bundle.role] ?? bundle.role}: ${entry.count} шт.` + (entry.loadedAmmo > 0 ? `, в магазине ${entry.loadedAmmo}` : ''),
        source: true,
      });
    }
  }
  for (const recipe of model.recipes) {
    if (recipe.result !== item.id) continue;
    // InventoryCombinationDef.IsValid: три разных непустых предмета — иначе отчёт рецепт не засчитывает.
    const valid = recipe.first !== null && recipe.second !== null && recipe.first !== recipe.second && recipe.first !== item.id && recipe.second !== item.id;
    places.push({
      kind: 'recipe',
      asset: recipe.asset,
      text: `рецепт: ${recipe.first ?? '?'} + ${recipe.second ?? '?'}` + (valid ? '' : ' (невалиден — не источник)'),
      source: valid,
    });
  }
  // Находки этажа отдельной механикой, не таблицей лута (ContentValidator.ReportReachability):
  // слот ПМ — если хоть один назначенный бюджет даёт шанс > 0 (засчитывается один раз, как в Unity);
  // находки Веры — если шанс в NurseHubBalanceDef > 0.
  if (model.pistolFloorFind !== null && model.pistolFloorFind === item.id) {
    const ordered = [...model.budgets].sort((a, b) => Number(b.fallback) - Number(a.fallback));
    const budget = ordered.find((candidate) => candidate.pistolFindChanceUnarmedPercent > 0 || candidate.pistolSpareFindChancePercent > 0);
    if (budget !== undefined)
      places.push({
        kind: 'floor',
        asset: budget.asset,
        text: `слот ПМ этажа: без ствола ${budget.pistolFindChanceUnarmedPercent}%, запасной ${budget.pistolSpareFindChancePercent}%`,
        source: true,
      });
  }
  const nurse = model.nurseBalance;
  if (nurse !== null) {
    if (nurse.keysFindChance > 0 && nurse.keysItemId === item.id)
      places.push({ kind: 'floor', asset: nurse.asset, text: `находка для Веры: связка ключей, ${nurse.keysFindChance}% на этаж`, source: true });
    if (nurse.fieldCaseFindChance > 0 && nurse.fieldCaseItemId === item.id)
      places.push({ kind: 'floor', asset: nurse.asset, text: `находка для Веры: выездная укладка, ${nurse.fieldCaseFindChance}% на этаж`, source: true });
  }
  const shop = model.shop.filter((entry) => entry.itemId === item.id);
  for (const entry of shop) {
    places.push({
      kind: 'shop',
      asset: entry.asset,
      text: entry.onSale ? `терминал хаба: ${entry.priceTalons} талонов за ${entry.count}` : 'терминал хаба: не продаётся (цена не утверждена или 0)',
      source: entry.onSale,
    });
  }

  // Источники — как ItemReachabilityReport: тайник из таблицы назначенной
  // категории подходящего акта, дроп назначенного врага, продаваемая запись
  // терминала, валидный рецепт, набор, паспорт модуля, бюджет, находка этажа.
  const sources: string[] = [];
  for (const line of stash) if (isAssignedTable(model, line.table)) sources.push(`тайник ${line.table.asset.name}`);
  // Дроп — по строкам своей таблицы назначенного врага, а не по доле: отчёт Unity
  // засчитывает строку, даже если вес или разворот тега дают ноль.
  for (const enemy of model.enemies) {
    if (!enemy.assigned || enemy.dropTable === null) continue;
    const table = model.tables.find((candidate) => candidate.asset.name === enemy.dropTable);
    if (table !== undefined && table.entries.some((entry) => rowGives(entry, item))) sources.push(`дроп ${enemy.asset.name}`);
  }
  for (const place of places) if (place.source) sources.push(`${place.kind} ${place.asset.name}`);

  const unreachable = item.inCatalog && item.enabled && sources.length === 0 && !item.sourcedByScript;

  const stashPerAct = stash.reduce<Estimate>((sum, line) => (line.perAct === null ? sum : add(sum, line.perAct)), ZERO);
  const dropsAlive = drops.some((drop) => drop.enemy.assigned && drop.perKill > 0);
  const otherSources = places.some((place) => place.source);
  const stashUnknown = stash.some((line) => line.status === 'active' && line.perAct === null);
  const starved = !unreachable && sources.length > 0 && !otherSources && !dropsAlive && !stashUnknown && stashPerAct.high === 0;

  return {
    item,
    aspects: item.aspects.map(aspectSummary),
    stash,
    drops,
    places,
    shop,
    sources,
    unreachable,
    starved,
    stashPerAct,
  };
}

export function summarizeAll(model: ItemsPayload): ItemSummary[] {
  return model.items.map((item) => summarizeItem(model, item));
}
